<template>
  <v-dialog v-model="isShowModal" width="60vw">
    <v-card class="pa-2">
      <v-card-title>
        <span class="headline font-weight-bold">
          シミュレーション結果（詳細）
        </span>
      </v-card-title>
      <v-card-text>
        <small class="red--text">
          シミュレーション結果は、配信金額を保証するものではありません
        </small>
      </v-card-text>
      <v-card-text>
        <div class="title pb-3">
          全体
        </div>
        <v-data-table
          :headers="baseHeaders"
          :items="tableDataByAll"
          hide-actions
          class="pb-2"
        >
          <template slot="items" slot-scope="props">
            <td v-if="menuSetting.enableBulkSetting">
              <AppNumberWithUnit
                :value="menuSetting.radius"
                unit="m"
                local-string
              />
            </td>
            <td v-else>
              個別設定
            </td>
            <td>
              <AppNumberWithUnit
                :value="props.item.uu"
                unit="uu"
                local-string
              />
            </td>
            <td>
              <AppNumberWithUnit
                :value="props.item.imps"
                unit="imp"
                local-string
              />
            </td>
            <td>
              <AppNumberWithUnit
                :value="props.item.clicks"
                unit="click"
                local-string
              />
            </td>
            <td>
              <AppNumberWithUnit :value="props.item.ctr" unit="%" />
            </td>
            <td>
              <AppNumberWithUnit
                :value="props.item.revenue"
                prefix="¥"
                local-string
              />
            </td>
          </template>
        </v-data-table>
        <p class="caption">
          ※ 全体の結果は、グループ間のUU数の重複を排除し集計しております。<br />
          そのため、グループ別の結果の累計とは、数字が異なる場合がございます。
        </p>
      </v-card-text>
      <v-card-text>
        <div class="title pb-3">
          グループ別
        </div>
        <div class="scrollable">
          <v-data-table
            :headers="headersByGroup"
            :items="tableDataByGroup"
            hide-actions
            class="pb-2"
          >
            <template slot="items" slot-scope="props">
              <td>
                {{ props.item.group }}
              </td>
              <td>
                <AppNumberWithUnit
                  :value="props.item.radius"
                  unit="m"
                  local-string
                />
              </td>
              <td>
                <AppNumberWithUnit
                  :value="props.item.uu"
                  unit="uu"
                  local-string
                />
              </td>
              <td>
                <AppNumberWithUnit
                  :value="props.item.imps"
                  unit="imp"
                  local-string
                />
              </td>
              <td>
                <AppNumberWithUnit
                  :value="props.item.clicks"
                  unit="click"
                  local-string
                />
              </td>
              <td>
                <AppNumberWithUnit :value="props.item.ctr" unit="%" />
              </td>
              <td>
                <AppNumberWithUnit
                  :value="props.item.revenue"
                  prefix="¥"
                  local-string
                />
              </td>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
      <v-card-actions>
        <SimulationResultEncodeSelector v-model="selectedEncode" />
      </v-card-actions>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="onClickExportCsv">CSV出力</v-btn>
        <v-btn @click="closeModal">閉じる</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { SimulationResultHeaders as headers } from '@/models/Simulation'
import Encode from '@/constants/encode'
import SimulationResultEncodeSelector from './SimulationResultEncodeSelector'

export default {
  name: 'SimulationResultDetailModal',
  components: { SimulationResultEncodeSelector },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    simulationResult: {
      type: Object,
      default: () => {},
    },
    encode: {
      type: String,
      default: () => Encode.SHIFT_JIS,
    },
  },
  data() {
    return {
      sortable: false,
    }
  },
  computed: {
    isShowModal: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    selectedEncode: {
      get() {
        return this.encode
      },
      set(encode) {
        this.$emit('changeEncode', encode)
      },
    },
    baseHeaders() {
      return [
        {
          text: '半径',
          align: 'left',
          value: headers.RADIUS,
          sortable: this.sortable,
        },
        {
          text: 'ユーザー数',
          align: 'left',
          value: headers.UU,
          sortable: this.sortable,
        },
        {
          text: 'インプレッション',
          align: 'left',
          value: headers.IMPS,
          sortable: this.sortable,
        },
        {
          text: 'クリック',
          align: 'left',
          value: headers.CLICKS,
          sortable: this.sortable,
        },
        {
          text: 'CTR',
          align: 'left',
          value: headers.CTR,
          sortable: this.sortable,
        },
        {
          text: '消化見込み額',
          align: 'left',
          value: headers.REVENUE,
          sortable: this.sortable,
        },
      ]
    },
    headersByGroup() {
      return [
        {
          text: 'グループ',
          align: 'left',
          value: headers.GROUP,
          sortable: this.sortable,
        },
        ...this.baseHeaders,
      ]
    },
    resultsByAll() {
      return this.simulationResult.simulation
    },
    resultsByGroup() {
      return this.simulationResult.locationGroups
    },
    tableDataByAll() {
      return [
        {
          [headers.RADIUS]: this.resultsByAll.radius,
          [headers.UU]: this.resultsByAll.uu,
          [headers.IMPS]: this.resultsByAll.imps,
          [headers.CLICKS]: this.resultsByAll.clicks,
          [headers.CTR]: this.resultsByAll.ctr,
          [headers.REVENUE]: this.resultsByAll.revenue,
        },
      ]
    },
    tableDataByGroup() {
      return this.resultsByGroup.map(group => ({
        [headers.GROUP]: group.name,
        [headers.RADIUS]: group.radius,
        [headers.UU]: group.simulation.uu,
        [headers.IMPS]: group.simulation.imps,
        [headers.CLICKS]: group.simulation.clicks,
        [headers.CTR]: group.simulation.ctr,
        [headers.REVENUE]: group.simulation.revenue,
      }))
    },
    menuSetting() {
      return this.simulationResult.menuSetting
    },
  },
  methods: {
    closeModal() {
      this.$emit('input', false)
    },
    onClickExportCsv() {
      this.$emit('exportCsv')
    },
  },
}
</script>
<style scoped>
.scrollable {
  max-height: 200px;
  overflow-y: auto;
}
</style>
